<template>
  <div
    v-if="element.type === 'locked'"
    class="Content__Document PageLocked__Wrapper xs:!pb-0  mx-auto overflow-hidden pt-0"
    :class="{'mb-12 !pb-12': element.type === 'locked'}"
  >
    <document-page-locked
      :element="element"
    />
  </div>
  <aw-drag
    v-else
    :enable="bcDocumentStore.edit"
    :drag-item="element"
    #default="{ dragHandle, dragEl }"
    @drag-end="dndStore.dragEnd()"
    @drag-start="dndStore.dragStart(index)"
    @drag-will-start="dragWillStartHandler"
  >
    <!-- List of all elements in page-->
    <aw-drop
      :list="children"
      :can-drop-item="canDropItem"
      :create-placeholder="createPlaceholder"
      transition-name="document-elements"
      list-item-key="key"
      @drop-item="drop"
      class="Content__Document mx-auto"
      :class="{
        Content__DocumentEditOn: bcDocumentStore.edit,
        PageHasPlaceholder: element.placeholder,
        '!pt-0' : !isEveryElementLocked && element.locked
      }"
    >
      <template #header>
        <!-- Page name -->
        <div
          v-if="!isEveryElementLocked && element.locked"
          class="w-full bg-bc-blue flex flex-row justify-center space-x-2 p-4"
        >
          <aw-icon icon="lock" class="w-5 h-5 text-white " />
          <span class="text-white text-sm">{{ $t('Page is locked') }}</span>
        </div>

        <document-element-heading
          :element="element"
          :parent="element"
          :page="element"
          :index="0"
          :page-index="index"
          :drag-handle="dragHandle"
          :is-page-locked="element.locked"
          @edit-products="toggleEditProducts"
          :drag-el="dragEl"
          ref="heading"
        />
      </template>
      <template #default="{ item, index }">
        <component
          v-if="!isDraggingPage"
          :is="'document-element-' + item.type"
          :element="item"
          :parent="element"
          :page="element"
          :index="index"
          :key="item.key"
          :class="{ DocumentElementDragged: item.dragged, PageElements: true }"
          :is-page-locked="element.locked"
        />
      </template>
      <template #footer>
        <!-- Products / Eshop at the bottom page -->
        <document-element-eshop
          v-if="!isDraggingPage && (data.products?.length || editProducts)"
          :page="element"
          @onstockcolchanged="onstockcolchanged"
        />
      </template>
    </aw-drop>

    <!-- Merged Hot action -->
    <div
      class="Content__Document !p-0 !bg-transparent !shadow-none mx-auto"
      :class="{'h-8 lg:h-16': !(edit && !isDraggingPage)}"
      v-bc-get-box-model="{name: element.id ? 'hotAction' + element.id : null}"
    >
      <document-hot-action
        v-if="edit && !isDraggingPage"
        :parent="lastPageElement.parent"
        :page="element"
        :index="lastPageElement.elementIndex"
        :page-index="index + 1"
        :visible-permanently="parent.children.length - 1 === index"
        :last="parent.children.length - 1 === index"
      >
        {{ $t('add new element') }}
      </document-hot-action>
    </div>
  </aw-drag>
</template>

<script>
import BaseElement from '../../mixins/BaseElement'
import { mapStores } from 'pinia'
import useDndStore from '@/frontapp/store/dnd'
import elementsBoxModel from '../../elementsBoxModel'

export default {
  mixins: [BaseElement],
  emits: ['editProducts'],
  allowedChildElements: [
    'color',
    'embed',
    'files',
    'gallery',
    'header',
    'image',
    'locked',
    'preview',
    'table',
    'text'
  ],
  props: {
    isDraggingPage: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      editProducts: false,
      headingOffsetTopUpdated: null,
      showStock: false,
    }
  },
  methods: {
    dragWillStartHandler() {
      this.headingOffsetTopUpdated = this.$refs.heading.$el.offsetTop
      this.dndStore.dragWillStart(this.index)
    },
    onstockcolchanged(val) {
      this.showStock = val
      this.updated = 'showStock'
    },
    lastElementRecursion(element) {
      const childCnt = element.children.length
      if (childCnt > 0) {
        const lastElIndex = childCnt - 1
        const lastEl = element.children[lastElIndex]

        if (lastEl.type === 'header') {
          return this.lastElementRecursion(lastEl)
        } else {
          return {
            element: lastEl,
            elementIndex: lastElIndex + 1,
            parent: element
          }
        }
      }

      return {
        element: null,
        elementIndex: 0,
        parent: element
      }
    },
    toggleEditProducts() {
      this.editProducts = !this.editProducts
      this.$emit('editProducts', this.editProducts)
    },
    calculatePageDragPaddingers() {
      const pageHeight = elementsBoxModel['documentPage' + this.element.id].height
      const headingHeight = elementsBoxModel['elementHeading' + this.element.id].height
      const headingParentPaddingTop = elementsBoxModel['elementHeading' + this.element.id].parent.paddingTop
      const hotActionHeight = elementsBoxModel['hotAction' + this.element.id].height

      if (this.index < this.dndStore.currentPageIndex) {
        this.dndStore.pageDragPaddingTop += pageHeight - headingHeight - headingParentPaddingTop - hotActionHeight
      } else if (this.index === this.dndStore.currentPageIndex) {
        if (headingParentPaddingTop < this.headingOffsetTopUpdated) {
          this.dndStore.pageDragPaddingTop += this.headingOffsetTopUpdated
          this.dndStore.pageDragPaddingBottom += pageHeight - headingHeight - this.headingOffsetTopUpdated - hotActionHeight
        } else {
          this.dndStore.pageDragPaddingBottom += pageHeight - headingHeight - headingParentPaddingTop - hotActionHeight
        }
      } else {
        this.dndStore.pageDragPaddingBottom += pageHeight - headingHeight - headingParentPaddingTop - hotActionHeight
      }
    },
  },
  computed: {
    lastPageElement() {
      return this.lastElementRecursion(this.element)
    },
    ...mapStores(useDndStore),
  },
  mounted() {
    this.dndStore.clbs.add(this.calculatePageDragPaddingers)
  },
  unmounted() {
    this.dndStore.clbs.delete(this.calculatePageDragPaddingers)
  },
}
</script>

<style lang="scss" scoped>
.PageLocked__Wrapper {
  padding-top: 0;
}
</style>
